import { Box, Card, CardContent, Chip, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Person } from "@mui/icons-material";
import {
  formatTime12Hour,
  groupBySession,
  isClinicOpen,
  getFormattedDate,
} from "../common/util";

/**
 * @param {Object} props
 * @param {Object} props.availability - { doctors: [...], isHoliday: boolean }
 */
const Availability = ({ availability }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isOpen = isClinicOpen(availability?.doctors);

  const renderTimeChips = (slots) => {
    if (!slots?.length) return null;
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {slots.map((slot, index) => (
          <Chip
            key={index}
            label={`${formatTime12Hour(slot.startTime)} - ${formatTime12Hour(slot.endTime)}`}
            sx={{
              backgroundColor: theme.palette.mode === "dark" 
                ? "rgba(70, 255, 70, 0.15)" 
                : "rgba(152, 48, 47, 0.1)",
              color: theme.palette.text.primary,
              fontWeight: 500,
              fontSize: "0.875rem",
              height: "32px",
              borderRadius: "16px",
            }}
          />
        ))}
      </Box>
    );
  };

  const renderSessionGroup = (label, slots) => {
    if (!slots?.length) return null;
    return (
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            mb: 1,
            display: "block",
          }}
        >
          {t(label)}
        </Typography>
        {renderTimeChips(slots)}
      </Box>
    );
  };

  const renderDoctorCard = (doctor, index) => {
    const sessions = groupBySession(doctor.schedule);
    
    return (
      <Card
        key={index}
        sx={{
          mb: 2,
          mx: 2,
          borderRadius: "16px",
          boxShadow: theme.palette.mode === "dark" 
            ? "0 2px 8px rgba(0,0,0,0.3)" 
            : "0 2px 12px rgba(0,0,0,0.08)",
          border: theme.palette.mode === "dark" 
            ? "1px solid rgba(255,255,255,0.1)" 
            : "none",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {/* Doctor Info Row */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 2.5 }}>
            {/* Avatar */}
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                backgroundColor: theme.palette.mode === "dark" 
                  ? "rgba(70, 255, 70, 0.15)" 
                  : "rgba(152, 48, 47, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 2,
                flexShrink: 0,
              }}
            >
              <Person 
                sx={{ 
                  fontSize: 28, 
                  color: theme.palette.primary.main 
                }} 
              />
            </Box>
            {/* Name & Degree */}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: "1.125rem",
                  lineHeight: 1.3,
                }}
              >
                {doctor.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: "0.875rem",
                }}
              >
                {doctor.degree}
              </Typography>
            </Box>
          </Box>

          {/* Divider */}
          <Box
            sx={{
              height: "1px",
              backgroundColor: theme.palette.divider,
              mb: 2.5,
            }}
          />

          {/* Schedule Sessions */}
          {renderSessionGroup("morning", sessions.morning)}
          {renderSessionGroup("evening", sessions.evening)}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ textAlign: "center", py: 3, px: 2 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            mb: 0.5,
          }}
        >
          {t("todays-availability")}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
          <Typography
            variant="body1"
            sx={{ color: theme.palette.text.secondary }}
          >
            {getFormattedDate()}
          </Typography>
          {/* Status Indicator */}
          {!availability?.isHoliday && (
            <Chip
              size="small"
              label={isOpen ? t("open") : t("closed")}
              sx={{
                height: "22px",
                fontSize: "0.75rem",
                fontWeight: 600,
                backgroundColor: isOpen 
                  ? "rgba(46, 125, 50, 0.15)" 
                  : "rgba(211, 47, 47, 0.15)",
                color: isOpen ? "#2e7d32" : "#d32f2f",
                "& .MuiChip-label": {
                  px: 1,
                },
              }}
              icon={
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: isOpen ? "#2e7d32" : "#d32f2f",
                    ml: 1,
                  }}
                />
              }
            />
          )}
        </Box>
      </Box>

      {/* Content */}
      {availability?.isHoliday ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            pt: 4,
            pb: 2,
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.secondary,
            }}
          >
            {t("holiday")}
          </Typography>
        </Box>
      ) : (
        <Box>
          {availability?.doctors?.map((doctor, index) =>
            renderDoctorCard(doctor, index)
          )}
        </Box>
      )}
    </Box>
  );
};

export default Availability;
